import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { setSettings } from '../support/slices/tileBuilderSlice';
import { RootState, useAppDispatch } from '../support/store';
import { initialSettings } from '../types/tileBuilderTypes';
import { ensureNumber } from '../support/utils';
import {
  getEffectiveMargins,
  getEffectiveMarginPreset,
} from '../components/Admin/TileBuilder/TilePreview/tilePreviewHelpers';

export function useMarginInit(chartId: string) {
  const dispatch = useAppDispatch();

  const settings = useSelector(
    (state: RootState) => state.tileBuilder?.[chartId]?.tile?.settings || initialSettings
  );

  const displayContext = useSelector(
    (state: RootState) => state.tileBuilder?.[chartId]?.preview?.displayContext || 'preview'
  );

  useEffect(() => {
    const s = settings as any;
    const presetKey = getEffectiveMarginPreset(settings, displayContext);

    const defaults = {
      top: ensureNumber(settings.marginTop, 24),
      right: ensureNumber(settings.marginRight, 16),
      bottom: ensureNumber(settings.marginBottom, 48),
      left: ensureNumber(settings.marginLeft, 16),
    };

    const patch: Record<string, any> = {};

    // initialize new contextual presets if missing
    if (!s.dashboardBaseMargins) {
      patch.dashboardBaseMargins = s.baseMargins ?? defaults;
    }

    if (!s.dashboardLegendMargins) {
      patch.dashboardLegendMargins = s.legendMargins ?? defaults;
    }

    if (!s.leaderBaseMargins) {
      patch.leaderBaseMargins = s.baseMargins ?? defaults;
    }

    if (!s.leaderLegendMargins) {
      patch.leaderLegendMargins = s.legendMargins ?? defaults;
    }

    // if we initialized anything, persist it
    if (Object.keys(patch).length > 0) {
      dispatch(
        setSettings({
          chartId,
          settings: patch as any,
        })
      );
      return;
    }

    const effective = getEffectiveMargins(settings, displayContext);

    const nextTop = ensureNumber(effective.top, 24);
    const nextRight = ensureNumber(effective.right, 16);
    const nextBottom = ensureNumber(effective.bottom, 48);
    const nextLeft = ensureNumber(effective.left, 16);

    const currentTop = ensureNumber(settings.marginTop, 24);
    const currentRight = ensureNumber(settings.marginRight, 16);
    const currentBottom = ensureNumber(settings.marginBottom, 48);
    const currentLeft = ensureNumber(settings.marginLeft, 16);

    const presetMargins = s?.[presetKey];
    const presetTop = ensureNumber(presetMargins?.top, nextTop);
    const presetRight = ensureNumber(presetMargins?.right, nextRight);
    const presetBottom = ensureNumber(presetMargins?.bottom, nextBottom);
    const presetLeft = ensureNumber(presetMargins?.left, nextLeft);

    const flatMarginsChanged =
      currentTop !== nextTop ||
      currentRight !== nextRight ||
      currentBottom !== nextBottom ||
      currentLeft !== nextLeft;

    const presetNeedsNormalization =
      !presetMargins ||
      presetTop !== nextTop ||
      presetRight !== nextRight ||
      presetBottom !== nextBottom ||
      presetLeft !== nextLeft;

    if (flatMarginsChanged || presetNeedsNormalization) {
      dispatch(
        setSettings({
          chartId,
          settings: {
            marginTop: nextTop,
            marginRight: nextRight,
            marginBottom: nextBottom,
            marginLeft: nextLeft,
            [presetKey]: {
              top: nextTop,
              right: nextRight,
              bottom: nextBottom,
              left: nextLeft,
            },
          } as any,
        })
      );
    }
  }, [dispatch, chartId, settings, displayContext]);
}